import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from 'zustand';
import { useAuth } from '@/entities/Auth/store';
import { createAppChatService } from '@/entities/AppChat/api/appChatService';
import { createAppChatRouteStore } from '@/entities/AppChat/model/routeStore';
import { createChatDialogStore } from '@/entities/ChatDialog/model/store';
import { connectChatDialogRealtime } from '@/entities/ChatDialog/api/chatDialogRealtime';
import { ChatDialogWorkspaceContext } from '@/entities/ChatDialog/model/workspace';
import { useChatDialogRealtime } from '@/features/chatDialogRealtime/model/useChatDialogRealtime';
import type { ChatDialogRealtimeCallbacks } from '@/entities/ChatDialog/model/types';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { UserChatReader } from './UserChatReader';

/** Подключает выбранного владельца к Supabase для просмотра его переписки */
export const UserChatViewer = ({ ownerAppUserId }: { ownerAppUserId: string | null }) => {
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const canView = useCanAccessPermission('admin.chats.view');
    const enabled = canView && Boolean(ownerAppUserId);
    const service = useMemo(() => createAppChatService(ownerAppUserId ?? undefined), [ownerAppUserId]);
    const store = useMemo(() => createChatDialogStore(service), [service]);
    const routeStore = useMemo(() => createAppChatRouteStore(service), [service]);
    const routeError = useStore(routeStore, (state) => state.error);
    const [searchParams, setSearchParams] = useSearchParams();
    const legacyId = searchParams.get('chat_id') ?? searchParams.get('chatId');
    const dialogId = searchParams.get('dialogId');

    /** Подписывает только переписку выбранного владельца */
    const connect = useCallback((callbacks: ChatDialogRealtimeCallbacks) => (
        connectChatDialogRealtime(callbacks, service)
    ), [service]);

    const realtime = useChatDialogRealtime(enabled, undefined, store, connect);

    useEffect(() => () => store.getState().reset(), [store, sessionRevision]);

    useEffect(() => {
        if (!enabled || !legacyId || dialogId) return;
        let current = true;

        void routeStore.getState()
            .resolve(legacyId, null, false)
            .then((id) => {
                if (!current || !id) return;

                setSearchParams((previous) => {
                    const next = new URLSearchParams(previous);
                    next.delete('chat_id');
                    next.delete('chatId');
                    next.set('dialogId', id);

                    return next;
                }, { replace: true });
            });

        return () => {
            current = false;
            routeStore.getState().reset();
        };
    }, [enabled, legacyId, dialogId, routeStore, setSearchParams]);

    return <ChatDialogWorkspaceContext.Provider value={store}>
        <UserChatReader
            ownerAppUserId={ownerAppUserId ?? undefined}
            enabled={enabled}
            realtime={realtime}
            routeError={routeError}
        />
    </ChatDialogWorkspaceContext.Provider>;
};
